<script lang="ts">
    import {getCurrentLanguage, getLogger} from 'src/core';
    import './spreadsheet-viewer.styles.less';
    import type {MediaViewerFile} from 'src/features/media-viewer/lib/mv-types';
    import {createEventDispatcher, onMount} from 'svelte';
    import {isOnlineFile} from 'src/features/media-viewer/lib/mv-utils';
    import {exists} from 'shared/utils/custom-utils';
    import {Sheet} from 'svelte-sheets';
    import WorksheetSelectorToolbar from './WorksheetSelectorToolbar.svelte';
    import svelteSheetsOptions from './svelte-sheets.options';
    import * as XLSX from 'xlsx';
    import {unbindSvelteSheetsHotkeys} from './svelte-sheets-cleenup';

    export let file: MediaViewerFile;

    const sourceUrl = isOnlineFile(file) ? file.sourceUrl : `/files/${file.id}`;

    const logger = getLogger();
    const locale = getCurrentLanguage();
    const dispatch = createEventDispatcher<{
        'load-start': void,
        'load-success': void,
        'load-error': void
    }>();

    // Workbook data
    let workSheets: Record<string, XLSX.WorkSheet> = {};
    let activeSheetName = '';

    // Sheet data
    let data: (string | number | boolean | null)[][] = [];
    let columns: { width?: string }[] = [];
    const mergeCells: Record<string, [number, number]> = {};
    const style: Record<string, string> = {};
    const options = svelteSheetsOptions as any; // As any, because this library is fucked 😭🙏

    // Load sheet data from an active sheet name
    $: loadSheet(workSheets, activeSheetName);

    onMount(async () => {
        unbindSvelteSheetsHotkeys(); // remove all hotkeys on window or document to prevent conflicts with record grid
        try {
            await loadExcel();
            dispatch('load-success');
        } catch (err) {
            logger.error('Excel viewer load failed:', err);
            dispatch('load-error');
        }
    });

    async function loadExcel() {
        dispatch('load-start');

        const arrayBuffer = await fetch(sourceUrl).then((r) => {
            if (!r.ok) {
                throw new Error(`HTTP ${r.status}`);
            }

            return r.arrayBuffer();
        });

        const workbook = XLSX.read(arrayBuffer, {
            type: 'array',
            cellDates: true
        });

        workSheets = workbook.SheetNames.reduce((acc, sheetName) => {
            acc[sheetName] = workbook.Sheets[sheetName];
            return acc;
        }, {} as Record<string, XLSX.WorkSheet>);

        activeSheetName = workbook.SheetNames[0];
    }

    function loadSheet(sheets: Record<string, XLSX.WorkSheet> | null, sheetName: string) {
        if (!exists(sheets)) {
            return;
        }

        const sheet = sheets[sheetName];
        if (!exists(sheet)) {
            return;
        }

        const jsonSheet = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            blankrows: false,
            defval: null,
            raw: true
        });

        const maxCols = data.reduce((max, row) => Math.max(max, row.length), 0);
        const cols = Array.from({length: maxCols}, () => ({
            width: '100px'
        }));

        // Reassign all at the same time
        data = (jsonSheet as any[][]).map((row) =>
            row.map((cell) =>
                cell instanceof Date
                    ? cell.toLocaleDateString(locale)
                    : cell
            )
        );
        columns = cols;
    }

    const handleWorksheetChange = (event: CustomEvent<string>) => {
        if (event.detail === activeSheetName) {
            return;
        }

        activeSheetName = event.detail;
    };
</script>

<div class="mv-spreadsheet-viewer">
    <Sheet {data}
           {columns}
           {mergeCells}
           {style}
           {options}
           clipboard={['web application/json', 'text/plain']}/>

    <WorksheetSelectorToolbar {workSheets}
                              {activeSheetName}
                              on:worksheet-change={handleWorksheetChange}/>
</div>